/**
 * AudioRecorder test / demo page.
 *
 * Used for manual QA and Playwright visual testing.
 * Not included in production routing — only used during development.
 *
 * Access via: /test/audio-recorder (see App.tsx test route)
 */
import { useState } from 'react';
import { AudioRecorder, type AudioRecorderProps } from '../AudioRecorder.tsx';
import type { AudioRecordingResult } from '../../hooks/useAudioRecording.ts';

export function AudioRecorderTestPage() {
  const [results, setResults] = useState<AudioRecordingResult[]>([]);
  const [playbackUrl, setPlaybackUrl] = useState<string | null>(null);

  const handleComplete: AudioRecorderProps['onRecordingComplete'] = (result) => {
    setResults((prev) => [...prev, result]);
    // Create playback URL for the latest recording
    const url = URL.createObjectURL(result.blob);
    setPlaybackUrl(url);
  };

  return (
    <div
      style={{
        maxWidth: 420,
        margin: '40px auto',
        padding: '20px',
        fontFamily: 'system-ui, sans-serif',
      }}
    >
      <h1 style={{ fontSize: '1.25rem', marginBottom: 8 }}>
        AudioRecorder Test Page
      </h1>
      <p style={{ color: '#666', fontSize: '0.875rem', marginBottom: 24 }}>
        Tap the button below to test recording. The component handles
        permission requests and format detection automatically.
      </p>

      <AudioRecorder onRecordingComplete={handleComplete} />

      {/* Playback of latest recording */}
      {playbackUrl && (
        <div style={{ marginTop: 24 }}>
          <h2 style={{ fontSize: '1rem', marginBottom: 8 }}>Playback</h2>
          <audio controls src={playbackUrl} style={{ width: '100%' }} />
        </div>
      )}

      {/* Log of all recordings */}
      {results.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <h2 style={{ fontSize: '1rem', marginBottom: 8 }}>
            Recordings ({results.length})
          </h2>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {results.map((r, i) => (
              <li
                key={i}
                style={{
                  padding: '8px 12px',
                  background: '#f3f4f6',
                  borderRadius: 8,
                  marginBottom: 8,
                  fontSize: '0.8rem',
                  fontFamily: 'monospace',
                }}
              >
                <div>#{i + 1} — {r.mimeType}</div>
                <div>Duration: {r.duration.toFixed(1)}s</div>
                <div>Size: {(r.blob.size / 1024).toFixed(1)} KB</div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
